/**
 * Cache Engine — IndexedDB-backed Stale-While-Revalidate cache
 * for intelligence query results and chart visualizations.
 *
 * Strategy:
 *   1. Load cached result immediately (stale)
 *   2. Fetch fresh data in background
 *   3. If data changed, update cache + notify UI
 *
 * Stores both raw data AND pre-computed chart configs
 * so the CPU-intensive chart calculation is also cached.
 */

const DB_NAME = 'pnp_intelligence_cache';
const DB_VERSION = 1;
const STORE_NAME = 'query_results';
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes stale threshold

let _db = null;

function openDB() {
  return new Promise((resolve, reject) => {
    if (_db) { resolve(_db); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
      }
    };
    req.onsuccess = (e) => { _db = e.target.result; resolve(_db); };
    req.onerror = () => reject(req.error);
  });
}

function cacheKey(clientId, query) {
  return `${clientId}::${query.toLowerCase().trim().substring(0, 100)}`;
}

/**
 * Get cached result for a query. Returns null if not found.
 */
export async function getCached(clientId, query) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(cacheKey(clientId, query));
      req.onsuccess = () => {
        const result = req.result;
        if (!result) { resolve(null); return; }
        const age = Date.now() - result.cachedAt;
        resolve({ ...result, isStale: age > MAX_AGE_MS, ageMs: age });
      };
      req.onerror = () => resolve(null);
    });
  } catch { return null; }
}

/**
 * Store a query result + chart data in cache.
 */
export async function setCache(clientId, query, data) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put({
      cacheKey: cacheKey(clientId, query),
      clientId,
      query,
      data,
      cachedAt: Date.now(),
    });
  } catch { /* cache write is best-effort */ }
}

/**
 * Pre-fetch top queries for a client on login.
 * Runs in background, doesn't block UI.
 */
export async function prefetchTopQueries(clientId, queries, fetchFn) {
  for (const q of queries.slice(0, 3)) {
    const cached = await getCached(clientId, q);
    if (!cached || cached.isStale) {
      try {
        const result = await fetchFn(q);
        if (result && !result.error) {
          await setCache(clientId, q, result);
        }
      } catch { /* prefetch is best-effort */ }
    }
  }
}

/**
 * Clear all cache for a client.
 */
export async function clearClientCache(clientId) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.openCursor();
    req.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        if (cursor.value.clientId === clientId) cursor.delete();
        cursor.continue();
      }
    };
  } catch { /* best-effort */ }
}
